import { Alert, Box, Button, CircularProgress, Typography, alpha } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import theme from '../../../theme/theme';
import { Badge } from '../../../components/ui/Badge';
import { BADGE_VARIANTS } from '../../../components/ui/badgeVariants';
import { useGetPayslip } from '../hooks/useGetPayslip';
import { formatPayslipAmount, formatPayslipPeriod } from '../config/payslipFormat';
import { PAYSLIP_TYPE_VARIANT, payslipTypeKey } from '../config/payslipPresentation';
import type { PayslipDetailHeader } from '../payslipTypes';
import { PayslipFrozenHeader } from './PayslipFrozenHeader';
import { PayslipRubriques } from './PayslipRubriques';

const I18N = 'payments:payslips.detail';

/** Frozen net-to-pay banner (`bulletin.montant`) — never recalculated. */
function NetToPay({ header }: { header: PayslipDetailHeader }) {
  const { t, i18n } = useTranslation('payments');
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 3,
        py: 2,
        borderRadius: theme.borderRadius.md,
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      }}
    >
      <Typography variant="subtitle1" sx={{ color: theme.palette.text.body }}>
        {t(`${I18N}.netToPay`)}
      </Typography>
      <Typography
        sx={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: theme.typography.fontWeightBold,
          fontSize: '1.35rem',
          color: theme.palette.primary.main,
        }}
      >
        {formatPayslipAmount(header.montant, header.devise, '—', i18n.language)}
      </Typography>
    </Box>
  );
}

export interface PayslipDetailProps {
  employeeId: string;
  payslipId: string;
  onBack: () => void;
}

/**
 * Read-only payslip detail: a back bar (with the type badge), the header, the
 * rubriques ordered by pay plan, and the net-to-pay summary. Strictly read-only (no
 * create/edit/close/comment). PDF export is intentionally not offered (the
 * source will not provide it — HOMCO-204 §4.1).
 */
export function PayslipDetail({ employeeId, payslipId, onBack }: PayslipDetailProps) {
  const { t, i18n } = useTranslation('payments');
  const { data, loading, error } = useGetPayslip(employeeId, payslipId);

  const backButton = (
    <Button
      onClick={onBack}
      startIcon={<ArrowBackIcon />}
      size="small"
      sx={{ textTransform: 'none', color: theme.palette.text.label }}
    >
      {t(`${I18N}.back`)}
    </Button>
  );

  if (loading && !data) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>{backButton}</Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>{backButton}</Box>
        <Alert severity="error">{t(`${I18N}.loadError`)}</Alert>
      </Box>
    );
  }

  const { header } = data;
  const typeKey = payslipTypeKey(header);
  const typeColors = BADGE_VARIANTS[PAYSLIP_TYPE_VARIANT[typeKey]];

  return (
    <Box>
      {/* Detail bar — stays pinned at the top of the scroll area (like the employee
          SubHeader) so "Back to list" + the period title remain reachable while the
          rubriques scroll beneath it. Solid opaque background prevents see-through. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: theme.palette.common.white,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          pt: 1,
          pb: 2,
          mb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {backButton}
        <Typography
          variant="subtitle1"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            color: theme.palette.text.body,
          }}
        >
          {t(`${I18N}.title`, { month: formatPayslipPeriod(header.mois, i18n.language) })}
          <Badge
            label={t(`payments:payslips.type.${typeKey}`)}
            color={typeColors.color}
            bgcolor={typeColors.bgcolor}
          />
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.detailPage.gap }}>
        <PayslipFrozenHeader header={header} />
        <PayslipRubriques lines={data.lines} />
        <NetToPay header={header} />
      </Box>
    </Box>
  );
}
